import { useCallback, useMemo} from 'react';
import { useSelector } from 'react-redux';
import { GreenroomState as GS } from '../stores/types/types';
import { ConnectedUser } from '../stores/types/connection';
import { ProfileInterface, SafeProfileInterface } from '../stores/types/user-profiles';
import { ProfileMask } from '../stores/types/masks';
import logger from '../utils/logger-rum';

interface Props {
	suggestedProfile?: number | null | undefined;
	suggestedWebRtcId?: string | number | null | undefined;
};

export const buildProfileWithMask = (
	profile: ProfileInterface | null,
	mask: ProfileMask | null
): SafeProfileInterface => mask ? {
	email: profile?.email || '',
	gr_user: profile?.gr_user || 0,
	first_name: mask.first_name || '',
	last_name: mask.last_name || '',
	location: mask.location || '',
	avatar: mask.avatar || '',
	bg_image: mask.bg_image || '',
	title: mask.title || '',
	phone: mask.phone ?? profile?.phone,
} : {
	email: profile?.email || '',
	gr_user: profile?.gr_user || 0,
	first_name: profile?.first_name || '',
	last_name: profile?.last_name || '',
	location: profile?.location || '',
	avatar: profile?.avatar || '',
	bg_image: profile?.bg_image || '',
	title: profile?.title || '',
	phone: profile?.phone,
}

interface UseProfile {
	getMaskedProfileByWebRtcId: (webRTCId: string) => ProfileInterface | null;
	getMaskedProfileById: (profileId: number) => ProfileInterface | null;
	getMaskFromEmail: (email: string) => ProfileMask | null;
	watchedProfile: SafeProfileInterface | null;
	watchedIsMasked: boolean;
}

export default function useProfile(props: Props): UseProfile {	
	const connectedUsers = useSelector((s: GS) => s.SessionReducer.connectedUsers);
	const invitations = useSelector((s: GS) => s.InvitationsReducer.invitations);
	const profileMasks = useSelector((s: GS) => s.MasksReducer.profileMasks);
	const activeMasks = useSelector((s: GS) => s.MasksReducer.activeMasksEntity?.entityValue);
    const user = useSelector((s: GS) => s.SessionReducer.user);

	const getMaskFromEmail = useCallback((email: string) => {
		if (!activeMasks) return null;
		const maskId = activeMasks[email];
		const mask = profileMasks[maskId];
		return mask;
	}, [activeMasks, profileMasks]);

	const findMask = useCallback((profile?: ProfileInterface) => {
		const email = profile?.email;
		if (!email) return null;
		return getMaskFromEmail(email);
	}, [getMaskFromEmail]);
    
    const findInConnectedUsersByWebRtcId = useCallback((webRTCId: string) => {
		const webRtcIdUser = connectedUsers.find((u: ConnectedUser) => (
			(u.webrtcID === webRTCId || u.feedWebRTCIds?.includes(webRTCId))
			&& !u.onsiteLocationSession // users in onsite location sessions are not using profiles
		));

		if (!webRtcIdUser) return null;

		const newUser = {
			...webRtcIdUser,
			bg_image: webRtcIdUser.poster || '',
		}

		return newUser;
	}, [connectedUsers]);

    const findInConnectedUsersById = useCallback((profileId: number) => {
		const matchingUsers = connectedUsers.reduce((acc: ConnectedUser[], u: ConnectedUser) => {
			if (
				u.gr_user === profileId
				&& !u.onsiteLocationSession // users in onsite location sessions are not using profiles
			) {
				acc.push(u);
			}
			return acc;
		}, []);

		if (matchingUsers && matchingUsers.length > 1) {
			logger.warn('More than one user found with the same gr_user');
		}

		return {
			...matchingUsers[0],
			email: matchingUsers[0]?.email || '',
			gr_user: matchingUsers[0]?.gr_user || 0,
			bg_image: matchingUsers[0]?.poster || '',
		};
	}, [connectedUsers, user]);

	const findInInvites = useCallback((profileId: number): ProfileInterface | null => {
		if (!profileId || !invitations.length) return null;

		const inviteProfile = invitations.find((i) => i?.profile?.gr_user === profileId)?.profile;
		
		if (!inviteProfile?.gr_user) return null;

		const nProfile: ProfileInterface = {
			...inviteProfile,
			title: inviteProfile.job_title ?? '',
			bg_image: inviteProfile.poster ?? '',
			location: '',
		};
		
		return nProfile;
	}, [invitations]);

	const getMaskedProfileByWebRtcId = useCallback((webRTCId: string) => {
		const user = findInConnectedUsersByWebRtcId(webRTCId);
		if (!user) return null;
		const mask = findMask(user);
		return buildProfileWithMask(user, mask);
	}, [findInConnectedUsersByWebRtcId, findMask]);

	const getMaskedProfileById = useCallback((profileId: number) => {
		let user = findInConnectedUsersById(profileId) as ProfileInterface | null;
		if (!user?.email) {
			user = findInInvites(profileId);
		}
		if (!user) return null;
		const mask = findMask(user);
		return buildProfileWithMask(user, mask);
	}, [findInConnectedUsersById, findInInvites, findMask]);

	const getMaskedBaseUser = useCallback(() => {
		if (!user) return null;
		const mask = findMask(user);
		return buildProfileWithMask(user, mask);
	}, [user, findMask]);

	const watchedProfile: SafeProfileInterface | null = useMemo(() => {
		let profile = null;
		if (props.suggestedProfile) {
			profile = getMaskedProfileById(props.suggestedProfile);
		}
		if (!profile?.email && props.suggestedWebRtcId) {
			profile = getMaskedProfileByWebRtcId(props.suggestedWebRtcId.toString());
		}
		if (profile?.email) {
			return buildProfileWithMask(profile, null);
		}
		return null;
	}, [
		props.suggestedProfile,
		props.suggestedWebRtcId,
		getMaskedProfileByWebRtcId,
		getMaskedProfileById,
		getMaskedBaseUser
	]);

	const watchedIsMasked = useMemo(() => {
		if (!watchedProfile) return false;
		const mask = getMaskFromEmail(watchedProfile.email);
		return !!mask;
	}, [watchedProfile, getMaskFromEmail]);

    return {
		getMaskedProfileByWebRtcId,
		getMaskedProfileById,
		getMaskFromEmail,
		watchedProfile,
		watchedIsMasked,
	};
};